import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Clock } from 'lucide-react';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsSection = ({ data = {} }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const {
    title = 'Latest Tech News',
    subtitle = "Stay informed with compelling stories from NYC's tech scene",
    posts_count = 6,
    button_text = 'View All News',
    button_link = '/blog/'
  } = data;

  useEffect(() => {
    fetchBlogPosts();
  }, [posts_count]);

  const fetchBlogPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog?limit=${posts_count}`);
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Failed to fetch blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <section id="news" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{title}</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">{subtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-gray-100 rounded-2xl h-96 animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (posts.length === 0) {
    return null;
  }

  return (
    <section id="news" className="py-24 bg-white" data-testid="news-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{title}</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">{subtitle}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((post) => (
            <article
              key={post.id}
              className="group bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-gray-300 hover:shadow-xl transition-all duration-300"
              data-testid={`news-article-${post.id}`}
            >
              <Link to={`/blog/${post.slug}/`}>
                <div className="relative overflow-hidden">
                  {post.featured_image ? (
                    <img
                      src={post.featured_image}
                      alt={post.title}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                      <span className="text-4xl text-gray-400">{post.title[0]}</span>
                    </div>
                  )}
                  {post.category && (
                    <div className="absolute top-4 left-4">
                      <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold text-gray-900">
                        {post.category}
                      </span>
                    </div>
                  )}
                </div>
              </Link>
              <div className="p-6 space-y-4">
                <div className="flex items-center text-sm text-gray-500">
                  <Clock size={16} className="mr-2" />
                  {formatDate(post.published_at)}
                </div>
                <Link to={`/blog/${post.slug}/`}>
                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-gray-600 transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                </Link>
                <p className="text-gray-600 line-clamp-3">{post.excerpt}</p>
                <Link 
                  to={`/blog/${post.slug}/`}
                  className="text-gray-900 font-medium flex items-center hover:gap-2 transition-all"
                >
                  Read More
                  <ArrowRight size={16} className="ml-1" />
                </Link>
              </div>
            </article>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to={button_link}>
            <Button 
              variant="outline" 
              size="lg" 
              className="border-2 border-black text-black hover:bg-gray-50"
              data-testid="news-view-all-btn"
            >
              {button_text}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default NewsSection;
